/**
 * Types for the Question Bank system in the frontend
 */

/**
 * Status options for question bank items
 */
export enum QuestionBankItemStatus {
  DRAFT = 'DRAFT',
  PUBLISHED = 'PUBLISHED',
}

/**
 * Represents a category in the question bank system
 */
export interface QuestionBankCategory {
  id: string;
  name: string;
  description?: string;
  parentId?: string;
  level: number; // 0 for root categories, 1+ for subcategories
  color?: string; // Color for visual representation of the category
  createdAt: number;
  updatedAt: number;
  createdBy: string;
  isActive: boolean;
  isDeleted?: boolean; // Flag to mark categories as deleted without removing from DB
}

/**
 * Represents a line in a passage
 */
export interface QuestionBankLine {
  number: number;
  text: string;
}

/**
 * Represents an image in a passage or introduction
 */
export interface QuestionBankImage {
  url: string;
  alt: string;
}

/**
 * Represents a passage in a question bank item
 */
export interface QuestionBankPassage {
  id: string;
  questionBankItemId: string;
  title: string;
  lines: QuestionBankLine[];
  images?: QuestionBankImage[];
  orderIndex: number;
  createdAt: number;
  updatedAt: number;
}

/**
 * Represents a question option
 */
export interface QuestionBankQuestionOption {
  option: string;
  text: string;
}

/**
 * Represents a question in a question bank item
 */
export interface QuestionBankQuestion {
  id?: string;
  questionBankItemId: string;
  text: string;
  options: Record<string, string> | QuestionBankQuestionOption[];
  correctAnswer: string;
  explanation?: string;
  lineReference?: {
    start: number;
    end: number;
  };
  createdAt: number;
  updatedAt: number;
}

/**
 * Enum for iframe types
 */
export enum IframeType {
  NONE = "none",
  DESMOS_GRAPHING = "desmos_graphing",
  DESMOS_SCIENTIFIC = "desmos_scientific",
  DESMOS_FOUR_FUNCTION = "desmos_four_function", 
  DESMOS_GRAPH = "desmos_graph",
  CUSTOM = "custom" // For custom iframe content
}

/**
 * Represents a complex item in the question bank
 */
export interface QuestionBankItem {
  id?: string;
  categoryId: string;
  header: {
    title: string;
    subtitle?: string;
  };
  introduction: {
    title?: string;
    description?: string;
  };
  directions: string;
  passages?: QuestionBankPassage[];
  iframeContent?: string; // Field to store custom iframe HTML content
  iframeType?: IframeType; // Field to store the type of iframe to display
  question?: QuestionBankQuestion;
  difficulty: "easy" | "medium" | "hard";
  tags: string[];
  createdAt: number;
  updatedAt: number;
  createdBy?: string;
  isActive: boolean;
  status: QuestionBankItemStatus;
  isDeleted: boolean;
}

/**
 * Request to create a new question bank item
 */
export interface CreateQuestionBankItemRequest {
  categoryId: string;
  header?: {
    title?: string;
    subtitle?: string;
  };
  introduction?: {
    text?: string;
    images?: QuestionBankImage[];
  };
  directions?: string;
  passages?: Partial<QuestionBankPassage>[];
  question?: Partial<QuestionBankQuestion>;
  difficulty?: 'easy' | 'medium' | 'hard';
  tags?: string[];
  status?: QuestionBankItemStatus; // Default será DRAFT si no se especifica
}

/**
 * Request to update an existing question bank item
 */
export interface UpdateQuestionBankItemRequest {
  categoryId?: string;
  header?: {
    title?: string;
    subtitle?: string;
  };
  introduction?: {
    text?: string;
    images?: QuestionBankImage[];
  };
  directions?: string;
  passages?: Partial<QuestionBankPassage>[];
  question?: Partial<QuestionBankQuestion>;
  difficulty?: 'easy' | 'medium' | 'hard';
  tags?: string[];
  isActive?: boolean;
  status?: QuestionBankItemStatus; // DRAFT o PUBLISHED
  isDeleted?: boolean;
}

/**
 * Create category request interface
 */
export interface CreateCategoryRequest {
  name: string;
  description?: string;
  parentId?: string | null;
  color?: string; // Color for visual representation
}

/**
 * Update category request interface
 */
export interface UpdateCategoryRequest {
  name?: string;
  description?: string;
  parentId?: string | null;
  color?: string;
  isActive?: boolean;
}

/**
 * Create question bank item request interface
 */
export interface CreateQuestionBankItemRequest {
  categoryId: string;
  content: string;
  options?: string[];
  correctAnswer: string;
  explanation?: string;
  difficulty?: 'easy' | 'medium' | 'hard';
  tags?: string[];
}

/**
 * Update question bank item request interface
 */
export interface UpdateQuestionBankItemRequest {
  categoryId?: string;
  content?: string;
  options?: string[];
  correctAnswer?: string;
  explanation?: string;
  difficulty?: 'easy' | 'medium' | 'hard';
  tags?: string[];
  isActive?: boolean;
}

/**
 * Question category interface
 */
export interface QuestionCategory {
  id: string;
  name: string;
  description: string;
  parentId: null | string;
  level: number;
  createdAt: number;
  createdBy: string;
  isActive: boolean;
  isDeleted: boolean;
  color: string;
  updatedAt: number;
  subcategories: QuestionCategory[];
  totalQuestionCount?: number;
}

/**
 * Question bank test interface
 */
export interface QuestionBankTest {
  id: string;
  name: string;
  categoryId: string;
  sections: {
    id: string;
    title?: string;
    questions: {
      id: string;
      text: string;
      options: Record<string, string>;
      correctAnswer: string;
      explanation?: string;
    }[];
  }[];
  createdAt: number;
  createdBy: string;
  currentProgress?: {
    id: string;
    testId: string;
    answers: {
      questionId: string;
      selectedOption: string;
      answeredAt: number;
    }[];
    startedAt: number;
    completedAt?: number;
  };
  result?: {
    score: number;
    correctAnswers: number;
    incorrectAnswers: number;
    completedAt: number;
  };
}

/**
 * Question bank test answer interface
 */
export interface QuestionBankTestAnswer {
  questionId: string;
  selectedOption: string;
}
